import { useMutation, useQuery } from '@tanstack/react-query'
import { apiClient, getSessionTokenHeaders } from './client'
import type { EnrollmentDocumentType } from './types/enrollment'

const BASE_URL = '/api/v1/b2b-enrollment'

export type EnrollmentRequirementType = 'FIELD' | 'DOCUMENT'
export type EnrollmentRequirementInputType =
  'TEXT' | 'NUMBER' | 'DATE' | 'EMAIL' | 'PHONE' | 'CHECKBOX' | 'TEXT_AREA'

export interface EnrollmentRequirement {
  id: number
  code: string
  requirementType: EnrollmentRequirementType
  inputType?: EnrollmentRequirementInputType | null
  documentType?: EnrollmentDocumentType | null
  label: string
  helpText?: string | null
  required: boolean
  displayOrder?: number | null
  completed: boolean
  textValue?: string | null
  numberValue?: number | null
  dateValue?: string | null
  checkboxValue?: boolean | null
  fileName?: string | null
}

export type EnrollmentRequirementValue = {
  requirementId: number
  textValue?: string | null
  numberValue?: number | null
  dateValue?: string | null
  checkboxValue?: boolean | null
}

export const listEnrollmentRequirementsRequest = async (sessionToken: string) => {
  const response = await apiClient.get<EnrollmentRequirement[]>(
    `${BASE_URL}/session/${encodeURIComponent(sessionToken)}/requirements`,
    { headers: getSessionTokenHeaders(sessionToken) }
  )
  return response.data
}

export const updateEnrollmentRequirementsRequest = async (
  sessionToken: string,
  values: EnrollmentRequirementValue[]
) => {
  const response = await apiClient.put<EnrollmentRequirement[]>(
    `${BASE_URL}/session/${encodeURIComponent(sessionToken)}/requirements`,
    { values },
    { headers: getSessionTokenHeaders(sessionToken) }
  )
  return response.data
}

export const useEnrollmentRequirements = (sessionToken: string | null | undefined) =>
  useQuery({
    queryKey: ['enrollment', 'partner-requirements', sessionToken],
    queryFn: () => listEnrollmentRequirementsRequest(sessionToken!),
    enabled: !!sessionToken
  })

export const useUpdateEnrollmentRequirements = () =>
  useMutation({
    mutationFn: ({
      sessionToken,
      values
    }: {
      sessionToken: string
      values: EnrollmentRequirementValue[]
    }) => updateEnrollmentRequirementsRequest(sessionToken, values)
  })
